import { Descriptions, Grid, Tabs } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type RoleSpecsFragment } from '../../../api/fragments/RoleSpecs';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import RoleActions from './RoleActions';
import RolePermissionList from './RolePermissionList';
import RoleUserList from './RoleUserList';

export type RoleInnerPageProps = { role: RoleSpecsFragment };

const RoleInnerPage = ({ role }: RoleInnerPageProps) => {
    const { t } = useTranslation(['roleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: role.versioning,
        timeZone: role.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const [activeTab, setActiveTab] = useState('permissions');

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('roleDetails:content.displayName')}>{role.displayName}</Descriptions.Item>
            <Descriptions.Item label={t('roleDetails:content.company')}>{role.company.displayName}</Descriptions.Item>
            <Descriptions.Item label={t('roleDetails:content.permissionCount')}>
                {role.permissions.length}
            </Descriptions.Item>
            <Descriptions.Item label={t('roleDetails:content.userCount')}>{role.users.length}</Descriptions.Item>
            {role.description && (
                <Descriptions.Item label={t('roleDetails:content.description')}>{role.description}</Descriptions.Item>
            )}
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<RoleActions role={role} />}
            header={{
                footer: (
                    <Tabs activeKey={activeTab} onChange={setActiveTab}>
                        <Tabs.TabPane key="permissions" tab={t('roleDetails:tabs.permissions.title')} />
                        <Tabs.TabPane key="users" tab={t('roleDetails:tabs.users.title')} />
                    </Tabs>
                ),
            }}
            onBack={() => navigate('/admin/accesses/roles')}
            title={screens.md ? t('roleDetails:title', { name: role.displayName }) : role.displayName}
        >
            {(() => {
                switch (activeTab) {
                    case 'permissions':
                        return <RolePermissionList role={role} />;

                    case 'users':
                        return <RoleUserList role={role} />;

                    default:
                        return null;
                }
            })()}
        </ConsolePageWithHeader>
    );
};

export default RoleInnerPage;
